// TeamModal.js
// Modal for creating and editing a provider team, including member management.
// Opened by TeamsTab; receives team data and closes via onClose callback.

import React, { useState } from 'react';
import api from '../../services/api';
import Button from '../../components/Button';
import { ROLE_DISPLAY, styles } from './settingsShared';

export default function TeamModal({ team, providers, staffUsers, onClose }) {
  const isNew = !team.id;

  const [name, setName] = useState(team.name || '');
  const [leadProviderId, setLeadProviderId] = useState(
    team.lead_provider_id ? String(team.lead_provider_id) : ''
  );
  const [members, setMembers] = useState(
    team.members ? team.members.map(m => ({ ...m })) : []
  );
  const [addUserId, setAddUserId] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const memberUserIds = new Set(members.map(m => m.user_id));
  const availableStaff = staffUsers.filter(u => !memberUserIds.has(u.id));

  const handleAddMember = () => {
    if (!addUserId) return;
    const user = staffUsers.find(u => u.id === parseInt(addUserId));
    if (!user) return;
    setMembers(prev => [...prev, { user_id: user.id, is_primary: false, user, _new: true }]);
    setAddUserId('');
  };

  const handleRemoveMember = (userId) =>
    setMembers(prev => prev.filter(m => m.user_id !== userId));

  const handlePrimaryToggle = (userId) =>
    setMembers(prev =>
      prev.map(m => ({ ...m, is_primary: m.user_id === userId ? !m.is_primary : m.is_primary }))
    );

  const handleSave = async () => {
    if (!name) { setError('Team name is required.'); return; }
    if (!leadProviderId) { setError('Lead provider is required.'); return; }
    setSaving(true); setError('');

    try {
      let teamId = team.id;

      if (isNew) {
        const res = await api.post('/provider-teams/', {
          name, lead_provider_id: parseInt(leadProviderId),
        });
        teamId = res.data.id;
      } else {
        await api.patch(`/provider-teams/${teamId}`, {
          name, lead_provider_id: parseInt(leadProviderId),
        });
        const originalIds = new Set(team.members.map(m => m.user_id));
        const currentIds = new Set(members.map(m => m.user_id));
        for (const uid of originalIds) {
          if (!currentIds.has(uid)) {
            await api.delete(`/provider-teams/${teamId}/members/${uid}`);
          }
        }
      }

      for (const m of members) {
        if (m._new) {
          await api.post(`/provider-teams/${teamId}/members`, {
            user_id: m.user_id, is_primary: m.is_primary || null,
          });
        } else if (!isNew) {
          const original = team.members.find(om => om.user_id === m.user_id);
          if (original && original.is_primary !== m.is_primary) {
            await api.delete(`/provider-teams/${teamId}/members/${m.user_id}`);
            await api.post(`/provider-teams/${teamId}/members`, {
              user_id: m.user_id, is_primary: m.is_primary || null,
            });
          }
        }
      }

      onClose();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to save team.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={styles.modalOverlay}>
      <div style={styles.modal}>
        <div style={styles.modalHeader}>
          <h2 style={styles.modalTitle}>
            {isNew ? 'New Provider Team' : `Edit: ${team.name}`}
          </h2>
          <button style={styles.modalClose} onClick={onClose}>✕</button>
        </div>

        {error && <div style={{ ...styles.errorText, padding: '0 24px' }}>{error}</div>}

        {/* Team name and lead provider */}
        <div style={styles.modalSection}>
          <div style={styles.formGrid}>
            <div style={styles.fieldGroup}>
              <label style={styles.label}>Team Name *</label>
              <input style={styles.input} value={name} onChange={e => setName(e.target.value)} />
            </div>
            <div style={styles.fieldGroup}>
              <label style={styles.label}>Lead Provider *</label>
              <select
                style={styles.input}
                value={leadProviderId}
                onChange={e => setLeadProviderId(e.target.value)}
              >
                <option value="">— Select Provider —</option>
                {providers.map(p => (
                  <option key={p.id} value={p.id}>
                    {p.first_name} {p.last_name}{p.credential ? `, ${p.credential}` : ''}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {/* Current members */}
        <div style={styles.modalSection}>
          <div style={styles.modalSectionTitle}>Team Members</div>
          {members.length === 0 && (
            <div style={styles.emptyRow}>No members added yet.</div>
          )}
          {members.length > 0 && (
            <table style={styles.table}>
              <thead>
                <tr>
                  {['Name', 'Role', 'Primary Team', ''].map(h => (
                    <th key={h} style={styles.th}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {members.map(m => (
                  <tr key={m.user_id} style={styles.tr}>
                    <td style={styles.td}>
                      {m.user.first_name} {m.user.last_name}
                      {m.user.credential ? `, ${m.user.credential}` : ''}
                    </td>
                    <td style={styles.td}>{ROLE_DISPLAY[m.user.role] || m.user.role}</td>
                    <td style={styles.td}>
                      <input
                        type="checkbox"
                        checked={!!m.is_primary}
                        onChange={() => handlePrimaryToggle(m.user_id)}
                      />
                    </td>
                    <td style={styles.td}>
                      <Button
                        variant="danger"
                        size="sm"
                        onClick={() => handleRemoveMember(m.user_id)}
                      >
                        Remove
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {/* Add member */}
        <div style={styles.modalSection}>
          <div style={styles.modalSectionTitle}>Add Member</div>
          <div style={styles.addMemberRow}>
            <select
              style={{ ...styles.input, flex: 1 }}
              value={addUserId}
              onChange={e => setAddUserId(e.target.value)}
            >
              <option value="">— Select staff member —</option>
              {availableStaff.map(u => (
                <option key={u.id} value={u.id}>
                  {u.last_name}, {u.first_name}
                  {u.credential ? `, ${u.credential}` : ''} ({ROLE_DISPLAY[u.role]})
                </option>
              ))}
            </select>
            <Button onClick={handleAddMember} disabled={!addUserId}>
              Add
            </Button>
          </div>
          {availableStaff.length === 0 && (
            <div style={{
              fontSize: 'var(--font-size-xs)',
              color: 'var(--color-text-muted)',
              marginTop: '8px',
              fontFamily: 'var(--font-family-base)',
            }}>
              All eligible staff members are already on this team.
            </div>
          )}
        </div>

        <div style={styles.modalFooter}>
          <Button onClick={handleSave} disabled={saving}>
            {saving ? 'Saving...' : 'Save Changes'}
          </Button>
          <Button variant="secondary" onClick={onClose}>Cancel</Button>
        </div>
      </div>
    </div>
  );
}